import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { adminUpdate, adminUsersApi, deleteUser, registerCancellation } from '../../lib/adminApi';
import { calculateEffectiveEndDate, formatDateNL } from '../../lib/billingDates';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { UserBasicInfo } from '../../components/admin/UserBasicInfo';
import { AdminUserSocials } from '../../components/admin/AdminUserSocials';
import { AdminDomainAuthModal } from '../../components/AdminDomainAuthModal';
import { DomainSetupLinkModal } from '../../components/DomainSetupLinkModal';
import { WidgetConfigurationSection } from '../../components/WidgetConfigurationSection';
import { CustomSiteKoppelingSection } from '../../components/admin/CustomSiteKoppelingSection';
import { JoepaKoppelingSection } from '../../components/admin/JoepaKoppelingSection';
import { UsernameManager } from '../../components/UsernameManager';
import { CreditManagement } from '../../components/admin/CreditManagement';
import { HTMLViewModal } from '../../components/HTMLViewModal';
import { useUserRole } from '../../hooks/useUserRole';
import { ArrowLeft, User, Calendar, HardDrive, Shield, Code, Link as LinkIcon, Image as ImageIcon, Save, Clock, CheckCircle, XCircle, Mail, Palette, Upload, X, Trash2, AlertTriangle, Coins, Share2, Link2, Link2Off, List, ListOrdered, Eye, GraduationCap, RefreshCw, PlayCircle, ChevronDown, ChevronRight, Download, Users, Send, ToggleLeft, ToggleRight, Search, UserPlus, Compass, MessageSquare, Ban, UserCog, ExternalLink, FileText } from 'lucide-react';
import { getBlogUrlConfigForUser, generateBlogUrl } from '../../lib/blogUrlHelper';
import { WhatsAppConversation } from '../../components/WhatsAppConversation';
import { JourneyStatusStrip } from '../../components/klanttraject/JourneyStatusStrip';
import { JourneyMilestonesBlock } from '../../components/klanttraject/JourneyMilestonesBlock';
import { JourneyTimeline } from '../../components/klanttraject/JourneyTimeline';
import { JourneySignalsBlock } from '../../components/klanttraject/JourneySignalsBlock';
import { JourneyNotesBlock } from '../../components/klanttraject/JourneyNotesBlock';
import { PersonalMailModal } from '../../components/klanttraject/PersonalMailModal';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { formatBytes, compressImage } from '../../lib/imageCompression';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';

interface UserData {
  id: string;
  email: string;
  first_name: string;
  company_name: string;
  user_profile: string;
  website_url: string | null;
  user_number: number | null;
  image_preferences: string;
  username_slug: string | null;
  blog_schedule_enabled: boolean;
  blog_schedule_frequency: string | null;
  blog_schedule_days_of_week: number[];
  blog_schedule_time: string | null;
  blog_schedule_timezone: string | null;
  next_blog_scheduled_at: string | null;
  last_blog_sent_at: string | null;
  created_at: string;
  updated_at: string;
  sendgrid_from_email: string | null;
  sendgrid_from_name: string | null;
  sendgrid_domain_name: string | null;
  sendgrid_domain_verified: boolean;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  email_greeting_style: string;
  email_closing_style: string;
  is_pro: boolean;
  zapier_api_key: string;
  image_style?: string | null;
  image_elements_include?: string | null;
  image_elements_exclude?: string | null;
  image_color_preferences?: string | null;
  onboarding_status?: {
    completed: boolean;
    skipped: boolean;
    current_step: number | null;
    step_data: {
      step1?: any;
      step2?: any;
      step3?: any;
      step4?: any;
      step5?: any;
      step6?: any;
      step7?: any;
      step8?: any;
      step9?: any;
      step10?: any;
      step11?: any;
      step12?: any;
      followups?: {
        what_they_do?: string;
        target_audience?: string;
        problem_solution?: string;
        core_message?: string;
        tone?: string;
      };
    };
    completed_at: string | null;
    updated_at: string | null;
  };
  journey_status?: string | null;
  journey_last_activity_at?: string | null;
}

interface BlogSettings {
  id: string;
  user_id: string;
  logo_url: string | null;
  about_text: string;
  accent_color: string;
  blog_page_url: string | null;
  custom_domain: string | null;
  use_custom_domain: boolean | null;
  created_at: string;
  updated_at: string;
}

interface ImageFile {
  path: string;
  size: number;
  uploaded_at: string;
  url: string;
}

interface RecentBlog {
  id: string;
  slug: string;
  title: string;
  published_at: string | null;
  view_count: number | null;
  status: string;
  is_active: boolean;
  url: string | null;
}

type TabType = 'overview' | 'planning' | 'storage' | 'dns' | 'widget' | 'blog-url' | 'email-preferences' | 'blog-preferences' | 'credits' | 'onboarding' | 'klanttraject' | 'mailinglist' | 'socials' | 'subscription' | 'whatsapp' | 'account';

// Zelfde opzegredenen als de klant ziet in de self-service opzegflow.
const CANCEL_REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'too_expensive', label: 'Te duur' },
  { value: 'missing_features', label: 'Niet de juiste functies' },
  { value: 'wrong_fit', label: 'Werkt niet voor mijn type bedrijf' },
  { value: 'paused_business', label: 'Tijdelijk gestopt met ondernemen' },
  { value: 'switched_tool', label: 'Overgestapt naar een andere tool' },
  { value: 'other', label: 'Anders' },
];

interface EmailContact {
  id: string;
  email: string;
  name: string | null;
  subscribed: boolean;
  created_at: string;
  updated_at: string;
}

interface EmailCampaign {
  id: string;
  subject: string;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  total_contacts: number | null;
  sent_count: number | null;
  failed_count: number | null;
  created_at: string;
}

export function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useUserRole();
  const [loading, setLoading] = useState(true);

  const validTabs: TabType[] = ['overview', 'planning', 'storage', 'dns', 'widget', 'blog-url', 'email-preferences', 'blog-preferences', 'credits', 'onboarding', 'klanttraject', 'mailinglist', 'socials', 'subscription', 'whatsapp', 'account'];
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const activeTab: TabType = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'overview';

  const setActiveTab = (tab: TabType) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  const [user, setUser] = useState<UserData | null>(null);
  const [blogSettings, setBlogSettings] = useState<BlogSettings | null>(null);
  const [widgetApiKey, setWidgetApiKey] = useState<any>(null);
  const [latestBlog, setLatestBlog] = useState<any>(null);
  const [userImages, setUserImages] = useState<ImageFile[]>([]);
  const [storageTotal, setStorageTotal] = useState(0);
  const [loadingStorage, setLoadingStorage] = useState(false);
  const [showDomainAuthModal, setShowDomainAuthModal] = useState(false);
  const [showSetupLinkModal, setShowSetupLinkModal] = useState(false);
  const [setupLinkData, setSetupLinkData] = useState<{
    setupUrl: string;
    expiresAt: string;
  } | null>(null);
  const [generatingSetupLink, setGeneratingSetupLink] = useState(false);

  const [editingEmailPrefs, setEditingEmailPrefs] = useState(false);
  const [emailPrefsData, setEmailPrefsData] = useState({
    email_greeting_style: '',
    email_closing_style: '',
  });
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);
  const [emailPrefsSuccess, setEmailPrefsSuccess] = useState('');

  const greetingEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Underline,
    ],
    content: emailPrefsData.email_greeting_style,
    onUpdate: ({ editor }) => {
      setEmailPrefsData(prev => ({ ...prev, email_greeting_style: editor.getHTML() }));
    },
  });

  const closingEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Underline,
    ],
    content: emailPrefsData.email_closing_style,
    onUpdate: ({ editor }) => {
      setEmailPrefsData(prev => ({ ...prev, email_closing_style: editor.getHTML() }));
    },
  });

  const [editingBlogPrefs, setEditingBlogPrefs] = useState(false);
  const [blogPrefsData, setBlogPrefsData] = useState<BlogSettings | null>(null);
  const [blogPrefsSaving, setBlogPrefsSaving] = useState(false);
  const [blogPrefsSuccess, setBlogPrefsSuccess] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [recentBlogs, setRecentBlogs] = useState<RecentBlog[]>([]);
  const [recentBlogsLoading, setRecentBlogsLoading] = useState(false);
  const [recentBlogsLoaded, setRecentBlogsLoaded] = useState(false);

  const [editingSchedule, setEditingSchedule] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [activeCancellation, setActiveCancellation] = useState<{ cancelled_at: string; effective_end_date: string } | null>(null);
  const [cancelReasons, setCancelReasons] = useState<string[]>([]);
  const [cancelReasonOther, setCancelReasonOther] = useState('');
  const [cancelEndDate, setCancelEndDate] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);

  const [mailingContacts, setMailingContacts] = useState<EmailContact[]>([]);
  const [mailingCampaigns, setMailingCampaigns] = useState<EmailCampaign[]>([]);
  const [mailingLoading, setMailingLoading] = useState(false);
  const [mailingContactSearch, setMailingContactSearch] = useState('');
  const [mailingContactFilter, setMailingContactFilter] = useState<'all' | 'subscribed' | 'unsubscribed'>('all');
  const [togglingContactId, setTogglingContactId] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<{
    blogs_count: number;
    campaigns_count: number;
    contacts_count: number;
    storage_mb: number;
  } | null>(null);
  const [scheduleData, setScheduleData] = useState({
    blog_schedule_enabled: false,
    blog_schedule_days_of_week: [] as number[],
    blog_schedule_time: '08:00',
    blog_schedule_timezone: 'Europe/Amsterdam',
  });
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState('');

  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingSuccess, setOnboardingSuccess] = useState('');
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({});

  const [subscriptionFormAdmin, setSubscriptionFormAdmin] = useState<{
    id: string;
    is_active: boolean;
    title: string;
    description: string;
    button_text: string;
    success_title: string;
    success_message: string;
    confirmation_required: boolean;
    confirmation_subject: string;
    confirmation_message: string;
    gdpr_enabled: boolean;
    gdpr_text: string;
    gdpr_privacy_url: string | null;
  } | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [loadedTabs, setLoadedTabs] = useState<Set<TabType>>(new Set());

  const blogEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      Underline,
    ],
    content: blogPrefsData?.about_text || '',
    onUpdate: ({ editor }) => {
      setBlogPrefsData(prev => prev ? { ...prev, about_text: editor.getHTML() } : null);
    },
  });

  useEffect(() => {
    if (blogEditor && blogPrefsData && blogPrefsData.about_text !== blogEditor.getHTML()) {
      blogEditor.commands.setContent(blogPrefsData.about_text);
    }
  }, [blogPrefsData?.about_text, blogEditor]);

  useEffect(() => {
    if (greetingEditor && emailPrefsData.email_greeting_style && greetingEditor.getHTML() !== emailPrefsData.email_greeting_style) {
      greetingEditor.commands.setContent(emailPrefsData.email_greeting_style);
    }
  }, [emailPrefsData.email_greeting_style, greetingEditor]);

  useEffect(() => {
    if (closingEditor && emailPrefsData.email_closing_style && closingEditor.getHTML() !== emailPrefsData.email_closing_style) {
      closingEditor.commands.setContent(emailPrefsData.email_closing_style);
    }
  }, [emailPrefsData.email_closing_style, closingEditor]);

  useEffect(() => {
    if (userId) {
      Promise.all([loadUserData(true), loadUserStats()]);
    }
  }, [userId]);

  const loadUserData = async (showFullLoader = false) => {
    if (!userId) return;

    if (showFullLoader) {
      setLoading(true);
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session found');
        setLoading(false);
        return;
      }

      const userData = await adminUsersApi.detail(userId);

      if (userData) {
        setUser({
          ...userData,
          onboarding_status: undefined,
        });
        setBlogSettings(userData.blog_settings);
        setWidgetApiKey(userData.widget_api_key);
        setLatestBlog(userData.latest_blog || null);
        setScheduleData({
          blog_schedule_enabled: userData.blog_schedule_enabled || false,
          blog_schedule_days_of_week: userData.blog_schedule_days_of_week || [],
          blog_schedule_time: userData.blog_schedule_time || '08:00',
          blog_schedule_timezone: userData.blog_schedule_timezone || 'Europe/Amsterdam',
        });
        const greetingStyle = userData.email_greeting_style || '<p>Beste {{first_name|lezer}}</p>';
        const closingStyle = userData.email_closing_style || '<p>Met vriendelijke groet,<br>{{first_name}}<br>{{company_name}}</p>';

        setEmailPrefsData({
          email_greeting_style: greetingStyle,
          email_closing_style: closingStyle,
        });
        if (userData.blog_settings) {
          setBlogPrefsData(userData.blog_settings);
        } else {
          setBlogPrefsData({
            id: '',
            user_id: userId ?? '',
            logo_url: null,
            about_text: '',
            accent_color: '#FF6B35',
            blog_page_url: null,
            custom_domain: null,
            use_custom_domain: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      alert('Er is een fout opgetreden bij het laden van de gebruikersgegevens.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserStats = async () => {
    if (!userId) return;

    try {
      const [blogsRes, campaignsRes, contactsRes, storageRes] = await Promise.all([
        supabase
          .from('published_blogs')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .is('deleted_at', null),
        supabase
          .from('email_campaigns')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('email_contacts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('users')
          .select('storage_used_bytes')
          .eq('id', userId)
          .single()
      ]);

      setUserStats({
        blogs_count: blogsRes.count || 0,
        campaigns_count: campaignsRes.count || 0,
        contacts_count: contactsRes.count || 0,
        storage_mb: Math.round((storageRes.data?.storage_used_bytes || 0) / (1024 * 1024))
      });
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const loadUserImages = async () => {
    if (!userId) return;

    try {
      setLoadingStorage(true);

      const { data: logs, error } = await supabase
        .from('image_storage_logs')
        .select('*')
        .eq('user_id', userId)
        .eq('action', 'upload')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const images: ImageFile[] = logs?.map((log) => {
        const { data: urlData } = supabase.storage
          .from('blog-images')
          .getPublicUrl(log.file_path);

        return {
          path: log.file_path,
          size: log.file_size,
          uploaded_at: log.created_at,
          url: urlData.publicUrl,
        };
      }) || [];

      setUserImages(images);
      const total = images.reduce((sum, img) => sum + img.size, 0);
      setStorageTotal(total);
    } catch (error) {
      console.error('Error loading user images:', error);
    } finally {
      setLoadingStorage(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userId || !user) return;
    if (deleteConfirmText !== user.email) {
      alert('Voer het exacte emailadres in om te bevestigen');
      return;
    }

    try {
      setDeleting(true);

      // Volledige verwijdering server-side (service_role): public.users +
      // cascade + storage, auth.users + identities, Upload-Post-profiel en
      // WhatsApp-blokkade. Zie edge function `delete-user`.
      const result = await deleteUser(userId);

      const warn = result.warnings && result.warnings.length
        ? ' (Let op: ' + result.warnings.join(' ') + ')'
        : '';

      // Success - navigate back
      const basePath = role === 'support' ? '/support' : '/admin';
      navigate(`${basePath}/users`, {
        state: { message: 'Gebruiker succesvol verwijderd' + warn }
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Er is een fout opgetreden bij het verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  const loadActiveCancellation = async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('subscription_cancellations')
      .select('cancelled_at, effective_end_date')
      .eq('user_id', userId)
      .is('reversed_at', null)
      .order('cancelled_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setActiveCancellation(data ?? null);
  };

  useEffect(() => {
    loadActiveCancellation();
  }, [userId]);

  const openCancelModal = () => {
    setCancelReasons([]);
    setCancelReasonOther('');
    setCancelError(null);
    setCancelSuccess(null);
    setCancelEndDate(user?.created_at ? calculateEffectiveEndDate(user.created_at) : '');
    setShowCancelModal(true);
  };

  const toggleCancelReason = (value: string) => {
    setCancelReasons(prev =>
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
  };

  const handleRegisterCancellation = async () => {
    if (!userId) return;
    setCancelSubmitting(true);
    setCancelError(null);
    try {
      const result = await registerCancellation({
        user_id: userId,
        reasons: cancelReasons,
        reason_other: cancelReasonOther.trim() || undefined,
        effective_end_date: cancelEndDate || undefined,
      });
      setShowCancelModal(false);
      await loadActiveCancellation();
      setCancelSuccess(
        `Opzegging geregistreerd. Toegang tot ${formatDateNL(result.effective_end_date)}.` +
        ' Verschijnt in de hub na de eerstvolgende sync.'
      );
    } catch (error) {
      setCancelError(error instanceof Error ? error.message : String(error));
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!userId) return;

    try {
      setScheduleSaving(true);
      setScheduleSuccess('');

      await adminUpdate({
        table: 'users',
        operation: 'update',
        id: userId,
        data: {
          blog_schedule_enabled: scheduleData.blog_schedule_enabled,
          blog_schedule_frequency: 'weekly',
          blog_schedule_days_of_week: scheduleData.blog_schedule_days_of_week,
          blog_schedule_time: scheduleData.blog_schedule_time,
          blog_schedule_timezone: scheduleData.blog_schedule_timezone,
        },
      });

      setScheduleSuccess('Planning succesvol opgeslagen');
      setEditingSchedule(false);
      await loadUserData();

      setTimeout(() => setScheduleSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Fout bij opslaan planning');
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleMarkOnboardingComplete = async () => {
    if (!userId) return;

    try {
      setOnboardingLoading(true);
      setOnboardingSuccess('');

      await adminUpdate({
        table: 'user_onboarding_progress',
        operation: 'update',
        filters: { user_id: userId },
        data: {
          completed: true,
          completed_at: new Date().toISOString(),
        },
      });

      setOnboardingSuccess('Onboarding gemarkeerd als voltooid');
      await loadOnboardingData();

      setTimeout(() => setOnboardingSuccess(''), 3000);
    } catch (error) {
      console.error('Error marking onboarding complete:', error);
      alert('Fout bij opslaan onboarding status');
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleResetOnboarding = async () => {
    if (!userId) return;

    try {
      setOnboardingLoading(true);
      setOnboardingSuccess('');

      // RLS verbergt user_onboarding_progress rijen voor de admin (alleen owner
      // mag lezen). Daarom geen pre-check meer: altijd UPDATE via service-role
      // admin-update-data, en als geen rij wordt geraakt INSERT alsnog.
      const resetPayload = {
        completed: false,
        skipped: false,
        current_step: 1,
        step_data: {},
        completed_at: null,
      };

      const updateResult = await adminUpdate({
        table: 'user_onboarding_progress',
        operation: 'update',
        filters: { user_id: userId },
        data: resetPayload,
      });

      const updatedRows = Array.isArray(updateResult?.data) ? updateResult.data : [];
      if (updatedRows.length === 0) {
        await adminUpdate({
          table: 'user_onboarding_progress',
          operation: 'insert',
          data: { user_id: userId, ...resetPayload },
        });
      }

      setOnboardingSuccess('Onboarding wizard gereset');
      setShowResetConfirmModal(false);
      await loadOnboardingData();

      setTimeout(() => setOnboardingSuccess(''), 3000);
    } catch (error) {
      console.error('Error resetting onboarding:', error);
      const message = error instanceof Error ? error.message : 'onbekende fout';
      alert(`Fout bij resetten onboarding wizard: ${message}`);
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleDownloadOnboardingJson = () => {
    if (!user?.onboarding_status) return;

    const stepDefinitions = [
      {
        step: 'step1',
        label: 'Stap 1 — Bedrijfsnaam',
        questions: [{ key: 'business_name', question: 'Wat is de naam van jouw bedrijf?' }],
      },
      {
        step: 'step2',
        label: 'Stap 2 — Wat doet jouw bedrijf',
        questions: [{ key: 'what_they_do', question: 'Wat doet jouw bedrijf?' }],
        followup: { key: 'what_they_do', question: 'Doorvraag: Wat doen ze precies?' },
      },
      {
        step: 'step3',
        label: 'Stap 3 — Doelgroep',
        questions: [{ key: 'target_audience', question: 'Wie is jouw doelgroep?' }],
        followup: { key: 'target_audience', question: 'Doorvraag: Beschrijf je doelgroep nader' },
      },
      {
        step: 'step4',
        label: 'Stap 4 — Probleem & Oplossing',
        questions: [
          { key: 'problem', question: 'Welk probleem lost jouw bedrijf op?' },
          { key: 'solution', question: 'Wat is jouw oplossing?' },
        ],
        followup: { key: 'problem_solution', question: 'Doorvraag: Probleem & Oplossing' },
      },
      {
        step: 'step5',
        label: 'Stap 5 — Kernboodschap',
        questions: [{ key: 'core_message', question: 'Wat is de kernboodschap van jouw bedrijf?' }],
        followup: { key: 'core_message', question: 'Doorvraag: Kernboodschap' },
      },
      {
        step: 'step6',
        label: 'Stap 6 — Schrijfstijl',
        questions: [{ key: 'tone', question: 'Welke schrijfstijl past bij jouw bedrijf?' }],
        followup: { key: 'tone', question: 'Doorvraag: Schrijfstijl' },
      },
      {
        step: 'step7',
        label: 'Stap 7 — Veelgestelde vragen',
        questions: [{ key: 'faq_topics', question: 'Welke vragen stellen klanten vaak aan jou?' }],
      },
      {
        step: 'step8',
        label: 'Stap 8 — Website',
        questions: [{ key: 'website_url', question: 'Wat is de URL van jouw website?' }],
      },
      {
        step: 'step9',
        label: 'Stap 9 — Afbeeldingsstijl',
        questions: [{ key: 'image_style', question: 'Welke stijl past bij de afbeeldingen voor jouw bedrijf?' }],
      },
      {
        step: 'step10',
        label: 'Stap 10 — Elementen Toevoegen',
        questions: [{ key: 'image_elements_include', question: 'Welke elementen moeten terugkomen in de afbeeldingen?' }],
      },
      {
        step: 'step11',
        label: 'Stap 11 — Elementen Vermijden',
        questions: [{ key: 'image_elements_exclude', question: 'Welke elementen moeten worden vermeden in de afbeeldingen?' }],
      },
      {
        step: 'step12',
        label: 'Stap 12 — Kleurvoorkeuren',
        questions: [{ key: 'image_color_preferences', question: 'Welke kleurvoorkeuren gelden voor de afbeeldingen?' }],
      },
    ];

    const exportData = {
      user: {
        id: user.id,
        email: user.email,
        first_name: user.first_name,
        company_name: user.company_name,
      },
      onboarding_status: {
        completed: user.onboarding_status.completed,
        completed_at: user.onboarding_status.completed_at,
        skipped: user.onboarding_status.skipped,
        current_step: user.onboarding_status.current_step,
      },
      steps: stepDefinitions.map(({ step, label, questions, followup }) => {
        const stepData = user.onboarding_status!.step_data?.[step as keyof typeof user.onboarding_status.step_data] as any;
        const answers: Record<string, string | null> = {};

        questions.forEach(({ key }) => {
          answers[key] = stepData?.[key] ?? (user as any)[key] ?? null;
        });

        const result: Record<string, any> = {
          step,
          label,
          completed: !!stepData,
          answers,
        };

        if (followup) {
          const followupAnswer = user.onboarding_status?.step_data?.followups?.[followup.key as keyof typeof user.onboarding_status.step_data.followups];
          result.followup = {
            question: followup.question,
            answer: followupAnswer ?? null,
          };
        }

        return result;
      }),
    };

    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onboarding-${user.company_name || user.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveWidgetSettings = async (customDomain: string, useCustomDomain: boolean) => {
    if (!userId || !blogSettings) return;

    try {
      const { error } = await supabase
        .from('user_blog_settings')
        .update({
          custom_domain: customDomain || null,
          use_custom_domain: useCustomDomain,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (error) throw error;

      await loadUserData();
      alert('Widget instellingen opgeslagen');
    } catch (error) {
      console.error('Error saving widget settings:', error);
      throw error;
    }
  };

  const handleToggleWidget = async (enabled: boolean) => {
    if (!userId) return;

    try {
      await adminUpdate({
        table: 'users',
        operation: 'update',
        id: userId,
        data: {
          widget_enabled: enabled,
        },
      });

      await loadUserData();
    } catch (error) {
      console.error('Error toggling widget:', error);
      throw error;
    }
  };

  const handleSaveEmailPreferences = async () => {
    if (!userId) return;

    try {
      setEmailPrefsSaving(true);
      setEmailPrefsSuccess('');

      await adminUpdate({
        table: 'users',
        operation: 'update',
        id: userId,
        data: {
          email_greeting_style: emailPrefsData.email_greeting_style,
          email_closing_style: emailPrefsData.email_closing_style,
        },
      });

      setEmailPrefsSuccess('Email voorkeuren succesvol opgeslagen');
      setEditingEmailPrefs(false);
      await loadUserData();

      setTimeout(() => setEmailPrefsSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving email preferences:', error);
      alert('Fout bij opslaan email voorkeuren');
    } finally {
      setEmailPrefsSaving(false);
    }
  };

  const handleSaveBlogPreferences = async () => {
    if (!userId || !blogPrefsData) return;

    try {
      setBlogPrefsSaving(true);
      setBlogPrefsSuccess('');

      const { error } = await supabase
        .from('user_blog_settings')
        .upsert({
          user_id: userId,
          logo_url: blogPrefsData.logo_url,
          about_text: blogPrefsData.about_text,
          accent_color: blogPrefsData.accent_color,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setBlogPrefsSuccess('Blog voorkeuren succesvol opgeslagen');
      setEditingBlogPrefs(false);
      await loadUserData();

      setTimeout(() => setBlogPrefsSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving blog preferences:', error);
      alert('Fout bij opslaan blog voorkeuren');
    } finally {
      setBlogPrefsSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Upload alleen PNG, JPG, SVG of WEBP bestanden');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Logo mag maximaal 2MB zijn');
      return;
    }

    try {
      setUploadingLogo(true);

      let fileToUpload: File | Blob = file;
      if (file.type !== 'image/svg+xml') {
        const compressionResult = await compressImage(file, 800, 2, 'webp', 0.85);
        fileToUpload = new File(
          [compressionResult.blob],
          file.name.replace(/\.[^.]+$/, '.webp'),
          { type: 'image/webp' }
        );
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/logo-${Date.now()}.${fileExt}`;

      if (blogPrefsData?.logo_url) {
        const oldPath = blogPrefsData.logo_url.split('/').slice(-2).join('/');
        await supabase.storage.from('blog-logos').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('blog-logos')
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-logos')
        .getPublicUrl(fileName);

      setBlogPrefsData(prev => prev ? { ...prev, logo_url: publicUrl } : null);
      alert('Logo succesvol geupload');
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      alert(err.message || 'Kon logo niet uploaden');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!blogPrefsData?.logo_url) return;

    try {
      const oldPath = blogPrefsData.logo_url.split('/').slice(-2).join('/');
      await supabase.storage.from('blog-logos').remove([oldPath]);
      setBlogPrefsData(prev => prev ? { ...prev, logo_url: null } : null);
      alert('Logo verwijderd');
    } catch (err: any) {
      console.error('Error removing logo:', err);
      alert(err.message || 'Kon logo niet verwijderen');
    }
  };

  const handleGenerateSetupLink = async () => {
    if (!userId) return;

    setGeneratingSetupLink(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Niet geautoriseerd');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-domain-setup-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate setup link');
      }

      const data = await response.json();
      setSetupLinkData({
        setupUrl: data.setupUrl,
        expiresAt: data.expiresAt,
      });
      setShowSetupLinkModal(true);
    } catch (err: any) {
      console.error('Error generating setup link:', err);
      alert(err.message || 'Kon setup link niet genereren');
    } finally {
      setGeneratingSetupLink(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'storage' && userId && !loadedTabs.has('storage')) {
      loadUserImages();
      setLoadedTabs(prev => new Set(prev).add('storage'));
    }
  }, [activeTab, userId]);

  useEffect(() => {
    if (activeTab === 'onboarding' && userId && user && !user.onboarding_status) {
      loadOnboardingData();
    }
  }, [activeTab, userId]);

  useEffect(() => {
    if (activeTab === 'mailinglist' && userId && !loadedTabs.has('mailinglist')) {
      loadMailinglistData();
      setLoadedTabs(prev => new Set(prev).add('mailinglist'));
    }
  }, [activeTab, userId]);

  useEffect(() => {
    if (activeTab === 'subscription' && userId && !loadedTabs.has('subscription')) {
      loadSubscriptionData();
      setLoadedTabs(prev => new Set(prev).add('subscription'));
    }
  }, [activeTab, userId]);

  useEffect(() => {
    if (activeTab === 'blog-preferences' && userId && !blogSettings) {
      loadBlogSettings();
    }
    if (activeTab === 'blog-preferences' && userId && !recentBlogsLoaded) {
      setRecentBlogsLoaded(true);
      loadRecentBlogs();
    }
  }, [activeTab, userId]);

  const loadOnboardingData = async () => {
    if (!userId) return;
    // Service-role pad via admin-get-data — RLS op user_onboarding_progress
    // verbergt rijen voor admins die niet de owner zijn, dus directe supabase
    // client geeft altijd null voor andermans rij.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=user-onboarding-progress&userId=${userId}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } },
      );
      if (!res.ok) {
        console.error('Failed to load onboarding progress:', await res.text());
        return;
      }
      const body = await res.json();
      setUser(prev => prev ? { ...prev, onboarding_status: body?.data || undefined } : null);
    } catch (error) {
      console.error('Error loading onboarding progress:', error);
    }
  };

  const loadBlogSettings = async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('user_blog_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (data) {
      setBlogSettings(data);
      setBlogPrefsData(data);
    }
  };

  const loadRecentBlogs = async () => {
    if (!userId) return;
    setRecentBlogsLoading(true);
    try {
      const [urlConfig, blogsRes] = await Promise.all([
        getBlogUrlConfigForUser(userId, supabase),
        supabase
          .from('published_blogs')
          .select('id, slug, title, published_at, view_count, status, is_active, wp_post_url')
          .eq('user_id', userId)
          .is('deleted_at', null)
          .order('published_at', { ascending: false, nullsFirst: false })
          .limit(10),
      ]);
      const blogs = (blogsRes.data || []).map((b) => ({
        ...b,
        url: b.status === 'published' && b.is_active
          ? generateBlogUrl(b.slug, urlConfig, b.wp_post_url)
          : null,
      }));
      setRecentBlogs(blogs);
    } catch (error) {
      console.error('Error loading recent blogs:', error);
    } finally {
      setRecentBlogsLoading(false);
    }
  };

  const loadMailinglistData = async () => {
    if (!userId) return;
    setMailingLoading(true);
    try {
      const [contactsRes, campaignsRes] = await Promise.all([
        supabase
          .from('email_contacts')
          .select('id, email, name, subscribed, created_at, updated_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('email_campaigns')
          .select('id, subject, status, scheduled_at, sent_at, total_contacts, sent_count, failed_count, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(20),
      ]);
      setMailingContacts(contactsRes.data || []);
      setMailingCampaigns(campaignsRes.data || []);
    } catch (error) {
      console.error('Error loading mailinglist data:', error);
    } finally {
      setMailingLoading(false);
    }
  };

  const handleToggleContact = async (contactId: string, currentSubscribed: boolean) => {
    setTogglingContactId(contactId);
    try {
      const { error } = await supabase
        .from('email_contacts')
        .update({ subscribed: !currentSubscribed, updated_at: new Date().toISOString() })
        .eq('id', contactId);
      if (error) throw error;
      setMailingContacts(prev =>
        prev.map(c => c.id === contactId ? { ...c, subscribed: !currentSubscribed } : c)
      );
    } catch (error) {
      console.error('Error toggling contact:', error);
      alert('Er ging iets fout bij het wijzigen van de abonnementstatus');
    } finally {
      setTogglingContactId(null);
    }
  };

  const loadSubscriptionData = async () => {
    if (!userId) return;
    setSubscriptionLoading(true);
    try {
      const formRes = await supabase
        .from('subscription_forms')
        .select('id, is_active, title, description, button_text, success_title, success_message, confirmation_required, confirmation_subject, confirmation_message, gdpr_enabled, gdpr_text, gdpr_privacy_url')
        .eq('user_id', userId)
        .maybeSingle();
      setSubscriptionFormAdmin(formRes.data || null);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const tabs = [
    { id: 'overview' as TabType, label: 'Overzicht', icon: User },
    { id: 'onboarding' as TabType, label: 'Onboarding', icon: GraduationCap, isActive: user?.onboarding_status?.completed },
    { id: 'klanttraject' as TabType, label: 'Klanttraject', icon: Compass, isActive: !!user?.journey_status && user.journey_status !== 'inactive' },
    { id: 'credits' as TabType, label: 'Credits', icon: Coins },
    { id: 'mailinglist' as TabType, label: 'Mailinglijst', icon: Users, isActive: (userStats?.contacts_count ?? 0) > 0 },
    { id: 'email-preferences' as TabType, label: 'Email', icon: Mail },
    { id: 'blog-preferences' as TabType, label: 'Blog', icon: Palette },
    { id: 'planning' as TabType, label: 'Planning', icon: Calendar, isActive: user?.blog_schedule_enabled },
    { id: 'socials' as TabType, label: 'Socials', icon: Share2 },
    { id: 'storage' as TabType, label: 'Opslag', icon: HardDrive },
    { id: 'dns' as TabType, label: 'DNS', icon: Shield, isActive: user?.sendgrid_domain_verified },
    { id: 'widget' as TabType, label: 'Widget', icon: Code, isActive: !!widgetApiKey },
    { id: 'blog-url' as TabType, label: 'URL', icon: LinkIcon },
    { id: 'subscription' as TabType, label: 'Inschrijven', icon: UserPlus, isActive: !!subscriptionFormAdmin?.is_active },
    { id: 'whatsapp' as TabType, label: 'WhatsApp', icon: MessageSquare },
    ...(role === 'admin' ? [{ id: 'account' as TabType, label: 'Account', icon: UserCog, isActive: !!activeCancellation }] : []),
  ];

  const getDayName = (day: number) => {
    const days = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
    return days[day];
  };

  const toggleDay = (day: number) => {
    if (scheduleData.blog_schedule_days_of_week.includes(day)) {
      setScheduleData({
        ...scheduleData,
        blog_schedule_days_of_week: scheduleData.blog_schedule_days_of_week.filter(d => d !== day),
      });
    } else {
      setScheduleData({
        ...scheduleData,
        blog_schedule_days_of_week: [...scheduleData.blog_schedule_days_of_week, day].sort(),
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-900 font-medium">Gebruiker niet gevonden</p>
            <button
              onClick={() => {
                const basePath = role === 'support' ? '/support' : '/admin';
                navigate(`${basePath}/users`);
              }}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Terug naar gebruikers
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-4">
          <button
            onClick={() => {
              const basePath = role === 'support' ? '/support' : '/admin';
              navigate(`${basePath}/users`);
            }}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Terug naar gebruikers
          </button>
        </div>

        <div className="flex gap-0 bg-white border border-gray-200 rounded-xl min-h-[calc(100vh-180px)]">
          <div className="w-52 flex-shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col sticky top-4 self-start max-h-[calc(100vh-120px)] rounded-l-xl">
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-0.5">
                <h1 className="text-base font-semibold text-gray-900 truncate">{user.first_name}</h1>
                {user.user_number != null && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 text-xs font-mono font-medium flex-shrink-0">
                    #{user.user_number}
                  </span>
                )}
              </div>
              {user.company_name && (
                <p className="text-xs text-gray-500 truncate">{user.company_name}</p>
              )}
              <p className="text-xs text-gray-400 truncate mt-0.5">{user.email}</p>
            </div>

            <nav className="flex-1 p-2 overflow-y-auto">
              <div className="space-y-0.5">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const isFeatureActive = tab.isActive === true;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabType)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all text-left ${
                        isActive
                          ? 'bg-gray-900 text-white font-medium'
                          : isFeatureActive
                          ? 'text-green-700 hover:bg-green-50'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {Icon && (
                        <Icon className={`w-4 h-4 flex-shrink-0 ${
                          isActive ? 'text-white' : isFeatureActive ? 'text-green-600' : 'text-gray-400'
                        }`} />
                      )}
                      <span className="truncate">{tab.label}</span>
                      {isFeatureActive && !isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </nav>

          </div>

          <div className="flex-1 min-w-0 p-6">
          {cancelSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-start justify-between gap-3">
              <span>{cancelSuccess}</span>
              <button onClick={() => setCancelSuccess(null)} className="text-green-700 hover:text-green-900 flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {activeTab === 'overview' && (
            <UserBasicInfo user={user} onUpdate={loadUserData} />
          )}

          {activeTab === 'onboarding' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    Onboarding Status
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Bekijk en beheer de voortgang van de onboarding wizard voor deze gebruiker
                  </p>
                </div>
              </div>

              {onboardingSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-800">{onboardingSuccess}</p>
                </div>
              )}

              {!user.onboarding_status ? (
                <div className="text-center py-12 text-gray-600">
                  <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="font-medium text-gray-900 mb-2">Geen onboarding data</p>
                  <p className="text-sm mb-6">Deze gebruiker heeft de onboarding wizard nog niet gestart</p>
                  <button
                    onClick={() => setShowResetConfirmModal(true)}
                    disabled={onboardingLoading}
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Start / Reset Wizard
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`rounded-lg p-4 border-2 ${user.onboarding_status.completed ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className={`w-5 h-5 ${user.onboarding_status.completed ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className="font-semibold text-sm">Voltooid</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {user.onboarding_status.completed
                          ? `Afgerond op ${new Date(user.onboarding_status.completed_at!).toLocaleDateString('nl-NL')}`
                          : 'Nog niet voltooid'}
                      </p>
                    </div>

                    <div className={`rounded-lg p-4 border-2 ${user.onboarding_status.skipped ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <XCircle className={`w-5 h-5 ${user.onboarding_status.skipped ? 'text-yellow-600' : 'text-gray-400'}`} />
                        <span className="font-semibold text-sm">Overgeslagen</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {user.onboarding_status.skipped ? 'Gebruiker heeft onboarding overgeslagen' : 'Niet overgeslagen'}
                      </p>
                    </div>

                    <div className={`rounded-lg p-4 border-2 ${user.onboarding_status.current_step !== null && !user.onboarding_status.completed ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className={`w-5 h-5 ${user.onboarding_status.current_step !== null && !user.onboarding_status.completed ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="font-semibold text-sm">In Progress</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {user.onboarding_status.current_step !== null && !user.onboarding_status.completed
                          ? `Huidige stap: ${user.onboarding_status.current_step}`
                          : 'Niet gestart'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Wizard Stappen Voortgang</h3>
                    <div className="space-y-2">
                      {[
                        {
                          key: 'step1',
                          label: 'Stap 1 — Bedrijfsnaam',
                          fields: [
                            { key: 'business_name', label: 'Bedrijfsnaam' }
                          ]
                        },
                        {
                          key: 'step2',
                          label: 'Stap 2 — Wat doet jouw bedrijf',
                          fields: [
                            { key: 'what_they_do', label: 'Wat ze doen', multiline: true }
                          ],
                          followupKey: 'what_they_do',
                          followupLabel: 'Doorvraag: Wat doen ze precies?',
                        },
                        {
                          key: 'step3',
                          label: 'Stap 3 — Doelgroep',
                          fields: [
                            { key: 'target_audience', label: 'Doelgroep', multiline: true }
                          ],
                          followupKey: 'target_audience',
                          followupLabel: 'Doorvraag: Doelgroep',
                        },
                        {
                          key: 'step4',
                          label: 'Stap 4 — Probleem & Oplossing',
                          fields: [
                            { key: 'problem', label: 'Probleem', multiline: true },
                            { key: 'solution', label: 'Oplossing', multiline: true }
                          ],
                          followupKey: 'problem_solution',
                          followupLabel: 'Doorvraag: Probleem & Oplossing',
                        },
                        {
                          key: 'step5',
                          label: 'Stap 5 — Kernboodschap',
                          fields: [
                            { key: 'core_message', label: 'Kernboodschap', multiline: true }
                          ],
                          followupKey: 'core_message',
                          followupLabel: 'Doorvraag: Kernboodschap',
                        },
                        {
                          key: 'step6',
                          label: 'Stap 6 — Schrijfstijl',
                          fields: [
                            { key: 'tone', label: 'Tone of voice', multiline: true }
                          ],
                          followupKey: 'tone',
                          followupLabel: 'Doorvraag: Schrijfstijl',
                        },
                        {
                          key: 'step7',
                          label: 'Stap 7 — Veelgestelde vragen',
                          fields: [
                            { key: 'faq_topics', label: 'Veelgestelde vragen van klanten', multiline: true }
                          ]
                        },
                        {
                          key: 'step8',
                          label: 'Stap 8 — Website',
                          fields: [
                            { key: 'website_url', label: 'Website URL' }
                          ]
                        },
                        {
                          key: 'step9',
                          label: 'Stap 9 — Afbeeldingsstijl',
                          fields: [
                            { key: 'image_style', label: 'Stijl', multiline: true }
                          ]
                        },
                        {
                          key: 'step10',
                          label: 'Stap 10 — Elementen Toevoegen',
                          fields: [
                            { key: 'image_elements_include', label: 'Moet erin', multiline: true }
                          ]
                        },
                        {
                          key: 'step11',
                          label: 'Stap 11 — Elementen Vermijden',
                          fields: [
                            { key: 'image_elements_exclude', label: 'Moet eruit', multiline: true }
                          ]
                        },
                        {
                          key: 'step12',
                          label: 'Stap 12 — Kleurvoorkeuren',
                          fields: [
                            { key: 'image_color_preferences', label: 'Kleurvoorkeuren', multiline: true }
                          ]
                        },
                      ].map(({ key, label, fields, followupKey, followupLabel }) => {
                        const stepData = user.onboarding_status!.step_data?.[key as keyof typeof user.onboarding_status.step_data];
                        const hasUserFieldData = fields.some(({ key: fieldKey }) => !!(user as any)[fieldKey]);
                        const isCompleted = !!stepData || hasUserFieldData;
                        const isExpanded = expandedSteps[key];

                        return (
                          <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => setExpandedSteps(prev => ({ ...prev, [key]: !prev[key] }))}
                              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-600" />
                                )}
                                <span className="text-sm text-gray-700 font-medium">{label}</span>
                              </div>
                              {isCompleted ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400" />
                              )}
                            </button>

                            {isExpanded && isCompleted && (
                              <div className="p-4 bg-white border-t border-gray-200">
                                <div className="space-y-3">
                                  {fields.map((field) => {
                                    const { key: fieldKey, label: fieldLabel } = field;
                                    const multiline = 'multiline' in field ? field.multiline : false;
                                    const stepValue = stepData ? (stepData as any)[fieldKey] : null;
                                    const value = stepValue || (user as any)[fieldKey] || null;
                                    if (!value) return null;

                                    return (
                                      <div key={fieldKey}>
                                        <div className="text-xs font-semibold text-gray-600 mb-1">{fieldLabel}</div>
                                        {multiline ? (
                                          <div className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 rounded p-3 border border-gray-200">
                                            {value}
                                          </div>
                                        ) : (
                                          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200">
                                            {value}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {followupKey && (() => {
                                    const followupAnswer = user.onboarding_status?.step_data?.followups?.[followupKey as keyof typeof user.onboarding_status.step_data.followups];
                                    if (!followupAnswer) return null;
                                    return (
                                      <div key={`followup-${followupKey}`} className="mt-2 pt-2 border-t border-blue-100">
                                        <div className="text-xs font-semibold text-blue-600 mb-1 flex items-center gap-1">
                                          <span>💬</span>
                                          {followupLabel}
                                        </div>
                                        <div className="text-sm text-gray-900 whitespace-pre-wrap bg-blue-50 rounded p-3 border border-blue-200">
                                          {followupAnswer}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}

                            {isExpanded && !isCompleted && (
                              <div className="p-4 bg-white border-t border-gray-200">
                                <p className="text-sm text-gray-500 italic">Deze stap is nog niet voltooid</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {user.onboarding_status.current_step !== null && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900">
                        <strong>Huidige stap:</strong> Stap {user.onboarding_status.current_step} van 13
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    {!user.onboarding_status.completed && (
                      <button
                        onClick={handleMarkOnboardingComplete}
                        disabled={onboardingLoading}
                        className="btn-primary flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {onboardingLoading ? 'Opslaan...' : 'Markeer als Voldaan'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowResetConfirmModal(true)}
                      disabled={onboardingLoading}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset Wizard
                    </button>
                    <button
                      onClick={handleDownloadOnboardingJson}
                      className="btn-secondary flex items-center gap-2 ml-auto"
                    >
                      <Download className="w-4 h-4" />
                      Download JSON
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'klanttraject' && user && (
            <KlanttrajectTab user={user} onMutated={() => loadUserData()} />
          )}

          {activeTab === 'socials' && userId && (
            <AdminUserSocials userId={userId} />
          )}

          {activeTab === 'credits' && userId && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Coins className="w-5 h-5" />
                  Credit Beheer
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Bekijk en beheer de credit balance van deze gebruiker
                </p>
              </div>
              <CreditManagement userId={userId} />
            </div>
          )}

          {activeTab === 'email-preferences' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email Voorkeuren
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Stel standaard aanhef en afsluiting in voor email campaigns van deze gebruiker
                  </p>
                </div>
                {!editingEmailPrefs && (
                  <button
                    onClick={() => setEditingEmailPrefs(true)}
                    className="btn-secondary"
                  >
                    Bewerken
                  </button>
                )}
              </div>

              {emailPrefsSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-800">{emailPrefsSuccess}</p>
                </div>
              )}

              {editingEmailPrefs ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aanhef Template
                    </label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1">
                        <button
                          onClick={() => greetingEditor?.chain().focus().toggleBold().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${greetingEditor?.isActive('bold') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Bold"
                        >
                          <strong>B</strong>
                        </button>
                        <button
                          onClick={() => greetingEditor?.chain().focus().toggleItalic().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${greetingEditor?.isActive('italic') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Italic"
                        >
                          <em>I</em>
                        </button>
                        <button
                          onClick={() => greetingEditor?.chain().focus().toggleUnderline().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${greetingEditor?.isActive('underline') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Underline"
                        >
                          <u>U</u>
                        </button>
                      </div>
                      <EditorContent
                        editor={greetingEditor}
                        className="prose max-w-none p-4 min-h-[80px] focus:outline-none"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Beschikbare placeholders:
                      <code className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">{'{{first_name|lezer}}'}</code>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Afsluiting Template
                    </label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1">
                        <button
                          onClick={() => closingEditor?.chain().focus().toggleBold().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${closingEditor?.isActive('bold') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Bold"
                        >
                          <strong>B</strong>
                        </button>
                        <button
                          onClick={() => closingEditor?.chain().focus().toggleItalic().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${closingEditor?.isActive('italic') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Italic"
                        >
                          <em>I</em>
                        </button>
                        <button
                          onClick={() => closingEditor?.chain().focus().toggleUnderline().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${closingEditor?.isActive('underline') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Underline"
                        >
                          <u>U</u>
                        </button>
                      </div>
                      <EditorContent
                        editor={closingEditor}
                        className="prose max-w-none p-4 min-h-[100px] focus:outline-none"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Beschikbare placeholders:
                      <code className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">{'{{first_name}}'}</code>
                      <code className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">{'{{company_name}}'}</code>
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 mb-2">
                      <strong>Tips:</strong>
                    </p>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>Gebruik <code className="px-1 py-0.5 bg-blue-100 rounded">{'{{first_name|lezer}}'}</code> voor aanhef met fallback</li>
                      <li>De templates worden automatisch toegevoegd aan alle gegenereerde email campaigns</li>
                      <li>Je kunt ze daarna nog handmalig aanpassen per campaign</li>
                    </ul>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveEmailPreferences}
                      disabled={emailPrefsSaving}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {emailPrefsSaving ? 'Opslaan...' : 'Opslaan'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingEmailPrefs(false);
                        const greetingStyle = user.email_greeting_style || '<p>Beste {{first_name|lezer}}</p>';
                        const closingStyle = user.email_closing_style || '<p>Met vriendelijke groet,<br>{{first_name}}<br>{{company_name}}</p>';
                        setEmailPrefsData({
                          email_greeting_style: greetingStyle,
                          email_closing_style: closingStyle,
                        });
                      }}
                      className="btn-secondary"
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-2">Aanhef Template</div>
                    <div
                      className="prose max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: user.email_greeting_style }}
                    />
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-2">Afsluiting Template</div>
                    <div
                      className="prose max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: user.email_closing_style }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'blog-preferences' && blogPrefsData && (
            <>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Blog Voorkeuren
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Personaliseer hoe de blog widget eruit ziet voor deze gebruiker
                  </p>
                </div>
                {!editingBlogPrefs && (
                  <button
                    onClick={() => setEditingBlogPrefs(true)}
                    className="btn-secondary"
                  >
                    Bewerken
                  </button>
                )}
              </div>

              {blogPrefsSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-800">{blogPrefsSuccess}</p>
                </div>
              )}

              {editingBlogPrefs ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload bedrijfslogo. Het logo wordt automatisch geschaald (max 300px breed, 120px hoog op overzicht). Ondersteund: PNG, JPG, SVG of WEBP (max 2MB).
                    </p>

                    {blogPrefsData.logo_url ? (
                      <div className="relative inline-block">
                        <img
                          src={blogPrefsData.logo_url}
                          alt="Logo preview"
                          className="w-48 h-auto max-h-32 object-contain border border-gray-200 rounded-lg p-2 bg-white"
                        />
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">
                            {uploadingLogo ? 'Uploaden...' : 'Klik om logo te uploaden'}
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                        />
                      </label>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Over Ons Tekst
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Tekst die bovenaan de blog overzichtspagina verschijnt
                    </p>

                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1 flex-wrap">
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleHeading({ level: 1 }).run()}
                          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-semibold ${blogEditor?.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''}`}
                          type="button"
                        >
                          H1
                        </button>
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleHeading({ level: 2 }).run()}
                          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-semibold ${blogEditor?.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''}`}
                          type="button"
                        >
                          H2
                        </button>
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleHeading({ level: 3 }).run()}
                          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-semibold ${blogEditor?.isActive('heading', { level: 3 }) ? 'bg-gray-300' : ''}`}
                          type="button"
                        >
                          H3
                        </button>
                        <div className="w-px bg-gray-300 mx-1" />
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleBold().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${blogEditor?.isActive('bold') ? 'bg-gray-300' : ''}`}
                          type="button"
                        >
                          <strong>B</strong>
                        </button>
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleItalic().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${blogEditor?.isActive('italic') ? 'bg-gray-300' : ''}`}
                          type="button"
                        >
                          <em>I</em>
                        </button>
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleUnderline().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${blogEditor?.isActive('underline') ? 'bg-gray-300' : ''}`}
                          type="button"
                        >
                          <u>U</u>
                        </button>
                        <div className="w-px bg-gray-300 mx-1" />
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleBulletList().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${blogEditor?.isActive('bulletList') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Bullet List"
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => blogEditor?.chain().focus().toggleOrderedList().run()}
                          className={`p-2 rounded hover:bg-gray-200 ${blogEditor?.isActive('orderedList') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Numbered List"
                        >
                          <ListOrdered className="w-4 h-4" />
                        </button>
                        <div className="w-px bg-gray-300 mx-1" />
                        <button
                          onClick={() => {
                            const url = window.prompt('Voer URL in:');
                            if (url) {
                              blogEditor?.chain().focus().setLink({ href: url }).run();
                            }
                          }}
                          className={`p-2 rounded hover:bg-gray-200 ${blogEditor?.isActive('link') ? 'bg-gray-300' : ''}`}
                          type="button"
                          title="Link toevoegen"
                        >
                          <Link2 className="w-4 h-4" />
                        </button>
                        {blogEditor?.isActive('link') && (
                          <button
                            onClick={() => blogEditor?.chain().focus().unsetLink().run()}
                            className="p-2 rounded hover:bg-gray-200 text-red-600"
                            type="button"
                            title="Link verwijderen"
                          >
                            <Link2Off className="w-4 h-4" />
                          </button>
                        )}
                        <div className="w-px bg-gray-300 mx-1" />
                        <button
                          onClick={() => setShowHtmlModal(true)}
                          className="p-2 rounded hover:bg-gray-200"
                          type="button"
                          title="View HTML"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                      <EditorContent
                        editor={blogEditor}
                        className="prose max-w-none p-4 min-h-[120px] focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Accentkleur
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Deze kleur wordt gebruikt voor links, quotes en buttons op de blog pagina en in email campaigns
                    </p>

                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={blogPrefsData.accent_color}
                        onChange={(e) => setBlogPrefsData({ ...blogPrefsData, accent_color: e.target.value })}
                        className="w-20 h-12 rounded-lg border border-gray-300 cursor-pointer"
                      />
                      <div className="flex-1">
                        <input
                          type="text"
                          value={blogPrefsData.accent_color}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                              setBlogPrefsData({ ...blogPrefsData, accent_color: value });
                            }
                          }}
                          placeholder="#FF6B35"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-transparent outline-none font-mono"
                        />
                      </div>
                    </div>

                    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="text-xs font-medium text-gray-700 mb-3">Preview:</p>
                      <div className="space-y-2">
                        <a
                          href="#"
                          style={{ color: blogPrefsData.accent_color }}
                          className="block hover:underline"
                          onClick={(e) => e.preventDefault()}
                        >
                          Voorbeeld link met accentkleur
                        </a>
                        <button
                          style={{ backgroundColor: blogPrefsData.accent_color }}
                          className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                          type="button"
                        >
                          Voorbeeld button
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveBlogPreferences}
                      disabled={blogPrefsSaving}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {blogPrefsSaving ? 'Opslaan...' : 'Opslaan'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingBlogPrefs(false);
                        if (blogSettings) {
                          setBlogPrefsData(blogSettings);
                        }
                      }}
                      className="btn-secondary"
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {blogPrefsData.logo_url && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-3">Logo</div>
                      <img
                        src={blogPrefsData.logo_url}
                        alt="Logo"
                        className="w-48 h-auto max-h-32 object-contain border border-gray-200 rounded-lg p-2 bg-white"
                      />
                    </div>
                  )}

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-2">Over Ons Tekst</div>
                    {blogPrefsData.about_text ? (
                      <div
                        className="prose max-w-none text-sm"
                        dangerouslySetInnerHTML={{ __html: blogPrefsData.about_text }}
                      />
                    ) : (
                      <p className="text-sm text-gray-500 italic">Geen tekst ingesteld</p>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-3">Accentkleur</div>
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-lg border border-gray-300"
                        style={{ backgroundColor: blogPrefsData.accent_color }}
                      />
                      <div>
                        <div className="font-mono text-sm text-gray-900">{blogPrefsData.accent_color}</div>
                        <div className="text-xs text-gray-600 mt-1">Gebruikt voor links en buttons</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 mt-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Laatste blogs
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  De 10 meest recente blogs van deze gebruiker
                </p>
              </div>

              {recentBlogsLoading ? (
                <p className="text-sm text-gray-500">Blogs laden...</p>
              ) : recentBlogs.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Deze gebruiker heeft nog geen blogs.</p>
              ) : (
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                  {recentBlogs.map((blog) => (
                    <div key={blog.id} className="flex items-center justify-between gap-4 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{blog.title}</div>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3">
                          {blog.published_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(blog.published_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {blog.view_count ?? 0}x bekeken
                          </span>
                        </div>
                      </div>
                      {blog.url ? (
                        <a
                          href={blog.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap shrink-0"
                        >
                          Bekijk
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">Niet openbaar</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            </>
          )}

          {activeTab === 'planning' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Reminder Planning</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Stel in wanneer gebruikers automatische reminders ontvangen voor nieuwe blogs
                  </p>
                </div>
                {!editingSchedule && (
                  <button
                    onClick={() => setEditingSchedule(true)}
                    className="btn-secondary"
                  >
                    Bewerken
                  </button>
                )}
              </div>

              {scheduleSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-800">{scheduleSuccess}</p>
                </div>
              )}

              {editingSchedule ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scheduleData.blog_schedule_enabled}
                        onChange={(e) => setScheduleData({ ...scheduleData, blog_schedule_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B35]"></div>
                    </label>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Automatische reminders ingeschakeld</span>
                      <p className="text-xs text-gray-500">Schakel planning in of uit</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Dagen van de week</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                        <label
                          key={day}
                          className="flex items-center gap-2 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            checked={scheduleData.blog_schedule_days_of_week.includes(day)}
                            onChange={() => toggleDay(day)}
                            disabled={!scheduleData.blog_schedule_enabled}
                            className="w-4 h-4 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35] disabled:opacity-50"
                          />
                          <span className={`text-sm text-gray-700 group-hover:text-[#FF6B35] transition-colors ${!scheduleData.blog_schedule_enabled ? 'opacity-50' : ''}`}>
                            {getDayName(day)}
                          </span>
                        </label>
                      ))}
                    </div>
                    {scheduleData.blog_schedule_enabled && scheduleData.blog_schedule_days_of_week.length === 0 && (
                      <p className="text-sm text-red-600 mt-2">
                        Selecteer minimaal één dag
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tijd</label>
                      <input
                        type="time"
                        value={scheduleData.blog_schedule_time}
                        onChange={(e) => setScheduleData({ ...scheduleData, blog_schedule_time: e.target.value })}
                        className="input-field"
                        disabled={!scheduleData.blog_schedule_enabled}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tijdzone</label>
                      <select
                        value={scheduleData.blog_schedule_timezone}
                        onChange={(e) => setScheduleData({ ...scheduleData, blog_schedule_timezone: e.target.value })}
                        className="input-field"
                        disabled={!scheduleData.blog_schedule_enabled}
                      >
                        <option value="Europe/Amsterdam">Amsterdam (CET/CEST)</option>
                        <option value="Europe/London">Londen (GMT/BST)</option>
                        <option value="America/New_York">New York (EST/EDT)</option>
                        <option value="America/Los_Angeles">Los Angeles (PST/PDT)</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveSchedule}
                      disabled={scheduleSaving}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {scheduleSaving ? 'Opslaan...' : 'Opslaan'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingSchedule(false);
                        setScheduleData({
                          blog_schedule_enabled: user.blog_schedule_enabled || false,
                          blog_schedule_days_of_week: user.blog_schedule_days_of_week || [],
                          blog_schedule_time: user.blog_schedule_time || '08:00',
                          blog_schedule_timezone: user.blog_schedule_timezone || 'Europe/Amsterdam',
                        });
                      }}
                      className="btn-secondary"
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Status</div>
                    <div className="flex items-center gap-2">
                      {user.blog_schedule_enabled ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-600">Actief</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-gray-400" />
                          <span className="font-medium text-gray-400">Inactief</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-2">Dagen</div>
                    <div className="flex flex-wrap gap-2">
                      {user.blog_schedule_days_of_week && user.blog_schedule_days_of_week.length > 0 ? (
                        user.blog_schedule_days_of_week.map((day) => (
                          <span
                            key={day}
                            className="px-3 py-1 bg-white border border-gray-200 rounded-lg text-sm text-gray-700"
                          >
                            {getDayName(day)}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500">Geen dagen ingesteld</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Tijd</div>
                      <div className="font-medium text-gray-900">
                        {user.blog_schedule_time || 'Niet ingesteld'}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Tijdzone</div>
                      <div className="font-medium text-gray-900">
                        {user.blog_schedule_timezone || 'Europe/Amsterdam'}
                      </div>
                    </div>
                  </div>

                  {user.next_blog_scheduled_at && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Volgende reminder gepland</p>
                        <p className="text-sm text-blue-700 mt-1">
                          {new Date(user.next_blog_scheduled_at).toLocaleString('nl-NL')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'storage' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Opslag Details</h2>

              {loadingStorage ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Totaal aantal bestanden</div>
                      <div className="text-2xl font-semibold text-gray-900">{userImages.length}</div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Totale opslag</div>
                      <div className="text-2xl font-semibold text-gray-900">{formatBytes(storageTotal)}</div>
                    </div>
                  </div>

                  {userImages.length > 0 ? (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Afbeeldingen</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {userImages.map((img) => (
                          <div key={img.path} className="relative group">
                            <img
                              src={img.url}
                              alt={img.path}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="128"%3E%3Crect fill="%23e5e7eb" width="200" height="128"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="12"%3ENiet beschikbaar%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            <div className="mt-2 text-xs text-gray-600">
                              <div className="truncate">{img.path.split('/').pop()}</div>
                              <div>{formatBytes(img.size)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-600">
                      <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p>Geen afbeeldingen gevonden</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'dns' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">DNS Configuratie</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Beheer SendGrid domain authentication voor deze gebruiker
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleGenerateSetupLink}
                    disabled={generatingSetupLink || !user.sendgrid_domain_name || user.sendgrid_domain_verified}
                    className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      !user.sendgrid_domain_name
                        ? 'Stel eerst een domein in via DNS Beheer'
                        : user.sendgrid_domain_verified
                        ? 'DNS is al geverifieerd'
                        : 'Genereer setup link voor gebruiker'
                    }
                  >
                    <LinkIcon className="w-4 h-4" />
                    {generatingSetupLink ? 'Genereren...' : 'Genereer Setup Link'}
                  </button>
                  <button
                    onClick={() => setShowDomainAuthModal(true)}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    DNS Beheer
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Domain</div>
                    <div className="font-medium text-gray-900">
                      {user.sendgrid_domain_name || 'Niet geconfigureerd'}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Verificatie Status</div>
                    <div className="flex items-center gap-2">
                      {user.sendgrid_domain_verified ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-600">Geverifieerd</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-5 h-5 text-yellow-600" />
                          <span className="font-medium text-yellow-600">Wacht op DNS</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {!user.sendgrid_domain_name && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      Klik op "DNS Beheer" om domain authentication in te stellen voor deze gebruiker.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'widget' && (
            <>
              <div className="mb-6">
                <JoepaKoppelingSection userId={userId!} />
              </div>
              <div className="mb-6">
                <CustomSiteKoppelingSection userId={userId!} />
              </div>
              {widgetApiKey ? (
                <WidgetConfigurationSection
                  userId={userId!}
                  apiKey={widgetApiKey.api_key}
                  widgetEnabled={widgetApiKey.widget_enabled}
                  lastUsed={widgetApiKey.last_used_at ?? undefined}
                  customDomain={blogSettings?.custom_domain ?? undefined}
                  useCustomDomain={blogSettings?.use_custom_domain ?? undefined}
                  usernameSlug={user.username_slug ?? undefined}
                  latestBlog={latestBlog}
                  onSave={handleSaveWidgetSettings}
                  onToggleWidget={handleToggleWidget}
                />
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="text-center py-12">
                    <Code className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Geen Widget API Key</h3>
                    <p className="text-gray-600 mb-4">
                      Deze gebruiker heeft nog geen widget API key. Een API key wordt automatisch aangemaakt wanneer de gebruiker de widget voor het eerst gebruikt.
                    </p>
                    <button
                      onClick={async () => {
                        if (!userId) return;
                        try {
                          const { data, error } = await supabase
                            .from('widget_api_keys')
                            .insert({
                              user_id: userId,
                              api_key: crypto.randomUUID(),
                              active: true,
                              widget_enabled: true
                            })
                            .select()
                            .single();

                          if (error) throw error;
                          setWidgetApiKey(data);
                        } catch (err) {
                          console.error('Error creating API key:', err);
                          alert('Er ging iets fout bij het aanmaken van de API key');
                        }
                      }}
                      className="btn-primary"
                    >
                      API Key Handmatig Aanmaken
                    </button>
                  </div>
                </div>
              )}
            </>
          )}


          {activeTab === 'blog-url' && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Blog URL Configuratie</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Stel de lees-nu.nl blog URL in voor gebruikers die de widget nog niet gebruiken
                </p>
              </div>

              <UsernameManager
                userId={userId!}
                currentUsername={user.username_slug}
                firstName={user.first_name}
                companyName={user.company_name}
                onUpdate={loadUserData}
              />
            </div>
          )}

          {activeTab === 'mailinglist' && (
            <div className="space-y-6">
              {mailingLoading ? (
                <div className="flex items-center justify-center h-48">
                  <LoadingSpinner />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <Users className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-600">Totaal contacten</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{mailingContacts.length}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="p-2 bg-green-50 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-600">Ingeschreven</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-2">
                        {mailingContacts.filter(c => c.subscribed).length}
                      </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="p-2 bg-red-50 rounded-lg">
                          <XCircle className="w-4 h-4 text-red-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-600">Uitgeschreven</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-2">
                        {mailingContacts.filter(c => !c.subscribed).length}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl">
                    <div className="p-5 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Contacten
                        </h2>
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                              type="text"
                              value={mailingContactSearch}
                              onChange={e => setMailingContactSearch(e.target.value)}
                              placeholder="Zoek op naam of email..."
                              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 w-52"
                            />
                          </div>
                          <select
                            value={mailingContactFilter}
                            onChange={e => setMailingContactFilter(e.target.value as typeof mailingContactFilter)}
                            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
                          >
                            <option value="all">Alle</option>
                            <option value="subscribed">Ingeschreven</option>
                            <option value="unsubscribed">Uitgeschreven</option>
                          </select>
                          <button
                            onClick={loadMailinglistData}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Vernieuwen"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {mailingContacts.length === 0 ? (
                      <div className="text-center py-16">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">Geen contacten</p>
                        <p className="text-sm text-gray-400 mt-1">Deze gebruiker heeft nog geen email contacten</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Contact</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Status</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Toegevoegd</th>
                              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Actie</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {mailingContacts
                              .filter(c => {
                                if (mailingContactFilter === 'subscribed') return c.subscribed;
                                if (mailingContactFilter === 'unsubscribed') return !c.subscribed;
                                return true;
                              })
                              .filter(c => {
                                if (!mailingContactSearch) return true;
                                const q = mailingContactSearch.toLowerCase();
                                return (
                                  c.email.toLowerCase().includes(q) ||
                                  (c.name || '').toLowerCase().includes(q)
                                );
                              })
                              .map(contact => (
                                <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-5 py-3">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {contact.name || <span className="text-gray-400 italic">Geen naam</span>}
                                      </p>
                                      <p className="text-xs text-gray-400 mt-0.5">{contact.email}</p>
                                    </div>
                                  </td>
                                  <td className="px-5 py-3">
                                    {contact.subscribed ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                                        <CheckCircle className="w-3 h-3" />
                                        Ingeschreven
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-medium">
                                        <XCircle className="w-3 h-3" />
                                        Uitgeschreven
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-5 py-3">
                                    <p className="text-xs text-gray-500">
                                      {new Date(contact.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                  </td>
                                  <td className="px-5 py-3 text-right">
                                    <button
                                      onClick={() => handleToggleContact(contact.id, contact.subscribed)}
                                      disabled={togglingContactId === contact.id}
                                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                                        contact.subscribed
                                          ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                          : 'bg-green-50 text-green-700 hover:bg-green-100'
                                      }`}
                                    >
                                      {togglingContactId === contact.id ? (
                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                      ) : contact.subscribed ? (
                                        <ToggleLeft className="w-3 h-3" />
                                      ) : (
                                        <ToggleRight className="w-3 h-3" />
                                      )}
                                      {contact.subscribed ? 'Uitschrijven' : 'Inschrijven'}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl">
                    <div className="p-5 border-b border-gray-200">
                      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Recente campaigns
                      </h2>
                    </div>

                    {mailingCampaigns.length === 0 ? (
                      <div className="text-center py-16">
                        <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">Geen campaigns</p>
                        <p className="text-sm text-gray-400 mt-1">Deze gebruiker heeft nog geen email campaigns verstuurd</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Onderwerp</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Status</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Ontvangers</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Verzonden</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {mailingCampaigns.map(campaign => (
                              <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-5 py-3">
                                  <p className="text-sm font-medium text-gray-900 max-w-xs truncate">{campaign.subject}</p>
                                  <p className="text-xs text-gray-400 mt-0.5 font-mono">{campaign.id.slice(0, 8)}</p>
                                </td>
                                <td className="px-5 py-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    campaign.status === 'completed' ? 'bg-green-50 text-green-700' :
                                    campaign.status === 'failed' ? 'bg-red-50 text-red-700' :
                                    campaign.status === 'sending' ? 'bg-blue-50 text-blue-700' :
                                    campaign.status === 'scheduled' ? 'bg-amber-50 text-amber-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {campaign.status === 'completed' ? 'Voltooid' :
                                     campaign.status === 'failed' ? 'Mislukt' :
                                     campaign.status === 'sending' ? 'Bezig' :
                                     campaign.status === 'scheduled' ? 'Gepland' :
                                     campaign.status === 'draft' ? 'Concept' :
                                     campaign.status}
                                  </span>
                                </td>
                                <td className="px-5 py-3">
                                  {campaign.total_contacts != null ? (
                                    <div className="text-sm">
                                      <span className="text-gray-900 font-medium">{campaign.sent_count ?? 0}</span>
                                      <span className="text-gray-400"> / {campaign.total_contacts}</span>
                                      {(campaign.failed_count ?? 0) > 0 && (
                                        <span className="ml-1 text-red-500 text-xs">({campaign.failed_count} mislukt)</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-5 py-3">
                                  <p className="text-xs text-gray-500">
                                    {campaign.sent_at
                                      ? new Date(campaign.sent_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
                                      : campaign.scheduled_at
                                      ? `Gepland: ${new Date(campaign.scheduled_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`
                                      : new Date(campaign.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </p>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              {subscriptionLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : !subscriptionFormAdmin ? (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                  <UserPlus className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500">Geen inschrijfformulier geconfigureerd</p>
                  <p className="text-xs text-gray-400 mt-1">De gebruiker heeft nog geen inschrijfformulier ingesteld.</p>
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-gray-500" />
                        Inschrijfformulier
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${subscriptionFormAdmin.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {subscriptionFormAdmin.is_active ? 'Actief' : 'Inactief'}
                        </span>
                        {subscriptionFormAdmin.confirmation_required && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            Dubbele opt-in
                          </span>
                        )}
                        {subscriptionFormAdmin.gdpr_enabled && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                            GDPR
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Titel formulier</p>
                          <p className="text-sm text-gray-900">{subscriptionFormAdmin.title}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Knoptekst</p>
                          <p className="text-sm text-gray-900">{subscriptionFormAdmin.button_text}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Omschrijving</p>
                        <p className="text-sm text-gray-700">{subscriptionFormAdmin.description}</p>
                      </div>

                      <div className="border-t border-gray-100 pt-4">
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Na inschrijving</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Succestittel</p>
                            <p className="text-sm text-gray-700">{subscriptionFormAdmin.success_title}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Succesbericht</p>
                            <p className="text-sm text-gray-700">{subscriptionFormAdmin.success_message}</p>
                          </div>
                        </div>
                      </div>

                      {subscriptionFormAdmin.confirmation_required && (
                        <div className="border-t border-gray-100 pt-4">
                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Bevestigingsemail</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Onderwerp</p>
                              <p className="text-sm text-gray-700">{subscriptionFormAdmin.confirmation_subject}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Bericht</p>
                              <p className="text-sm text-gray-700">{subscriptionFormAdmin.confirmation_message}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {subscriptionFormAdmin.gdpr_enabled && (
                        <div className="border-t border-gray-100 pt-4">
                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">GDPR</p>
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">GDPR tekst</p>
                              <p className="text-sm text-gray-700">{subscriptionFormAdmin.gdpr_text}</p>
                            </div>
                            {subscriptionFormAdmin.gdpr_privacy_url && (
                              <div>
                                <p className="text-xs text-gray-400 mb-1">Privacy URL</p>
                                <a href={/^https?:\/\//i.test(subscriptionFormAdmin.gdpr_privacy_url) ? subscriptionFormAdmin.gdpr_privacy_url : `https://${subscriptionFormAdmin.gdpr_privacy_url}`} target="_blank\" rel="noopener noreferrer\" className="text-sm text-blue-600 hover:underline break-all">
                                  {subscriptionFormAdmin.gdpr_privacy_url}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {user?.username_slug && (
                        <div className="border-t border-gray-100 pt-4">
                          <a
                            href={`/inschrijven/${user.username_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Bekijk publieke pagina
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'whatsapp' && userId && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-[#231F20] mb-4">WhatsApp-conversatie</h2>
              <WhatsAppConversation userId={userId} />
            </div>
          )}

          {activeTab === 'account' && role === 'admin' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Ban className="w-5 h-5" />
                      Opzegging
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Leg een opzegging vast voor deze gebruiker. De toegang blijft tot het einde van de betaalde periode.
                    </p>
                  </div>
                </div>
                {activeCancellation ? (
                  <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg text-sm">
                    <Ban className="w-4 h-4 flex-shrink-0" />
                    <span>Opgezegd, toegang tot {formatDateNL(activeCancellation.effective_end_date)}</span>
                  </div>
                ) : (
                  <button
                    onClick={openCancelModal}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium"
                  >
                    <Ban className="w-4 h-4" />
                    Opzegging registreren
                  </button>
                )}
              </div>

              <div className="bg-white border border-red-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-red-700 flex items-center gap-2">
                      <Trash2 className="w-5 h-5" />
                      Gebruiker verwijderen
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Verwijdert deze gebruiker en alle bijbehorende gegevens definitief. Dit kan niet ongedaan worden gemaakt.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Verwijder gebruiker
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {showDomainAuthModal && user && (
        <AdminDomainAuthModal
          isOpen={showDomainAuthModal}
          onClose={() => setShowDomainAuthModal(false)}
          userId={userId!}
          userEmail={user.email}
          userName={user.first_name}
          userCompany={user.company_name}
          onUpdate={loadUserData}
        />
      )}

      {showSetupLinkModal && setupLinkData && user && (
        <DomainSetupLinkModal
          isOpen={showSetupLinkModal}
          onClose={() => setShowSetupLinkModal(false)}
          setupUrl={setupLinkData.setupUrl}
          expiresAt={setupLinkData.expiresAt}
          businessName={user.company_name || user.first_name}
        />
      )}

      {showHtmlModal && blogPrefsData && (
        <HTMLViewModal
          html={blogPrefsData.about_text}
          onClose={() => setShowHtmlModal(false)}
          onApply={(html) => {
            setBlogPrefsData(prev => prev ? { ...prev, about_text: html } : null);
            if (blogEditor) {
              blogEditor.commands.setContent(html);
            }
          }}
          title="Blog About Text HTML"
        />
      )}

      {showResetConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Onboarding Wizard Resetten</h3>
                <p className="text-sm text-gray-600 mt-1">Weet je zeker dat je de onboarding wizard wilt resetten?</p>
              </div>
            </div>

            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-900">
                Dit zal alle onboarding voortgang wissen. De gebruiker kan de wizard opnieuw doorlopen.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirmModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={onboardingLoading}
              >
                Annuleren
              </button>
              <button
                onClick={handleResetOnboarding}
                className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={onboardingLoading}
              >
                {onboardingLoading ? 'Resetten...' : 'Reset Wizard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Ban className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Opzegging registreren</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Leg een opzegging vast die per mail of telefoon binnenkwam. De klant houdt toegang tot de einddatum.
                </p>
              </div>
            </div>

            {cancelError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {cancelError}
              </div>
            )}

            <div className="space-y-5">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-sm">
                <p><span className="font-medium">Naam:</span> {user.first_name}</p>
                <p><span className="font-medium">Email:</span> {user.email}</p>
                {user.company_name && (
                  <p><span className="font-medium">Bedrijf:</span> {user.company_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reden van opzeggen</label>
                <div className="grid grid-cols-1 gap-2">
                  {CANCEL_REASON_OPTIONS.map(opt => {
                    const checked = cancelReasons.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleCancelReason(opt.value)}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
                          checked
                            ? 'bg-amber-50 border-amber-300 text-amber-900'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                          checked ? 'bg-amber-500 border-amber-500' : 'bg-white border-gray-400'
                        }`}>
                          {checked && <CheckCircle className="w-3 h-3 text-white" />}
                        </span>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Toelichting <span className="font-normal text-gray-500">(bijv. wat de klant in de mail schreef)</span>
                </label>
                <textarea
                  value={cancelReasonOther}
                  onChange={(e) => setCancelReasonOther(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="Optionele toelichting"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Toegang tot en met
                </label>
                <input
                  type="date"
                  value={cancelEndDate}
                  onChange={(e) => setCancelEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Automatisch berekend op einde lopende betaalperiode. Pas alleen aan als je een andere afspraak hebt gemaakt.
                </p>
              </div>

              <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                Er gaat geen mail naar de klant. Deze registratie is bedoeld voor klanten die al per mail hebben opgezegd.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={cancelSubmitting}
              >
                Annuleren
              </button>
              <button
                onClick={handleRegisterCancellation}
                className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={cancelSubmitting}
              >
                {cancelSubmitting ? 'Bezig...' : 'Opzegging registreren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && user && userStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Gebruiker Permanent Verwijderen</h3>
                <p className="text-sm text-gray-600 mt-1">Deze actie kan NIET ongedaan gemaakt worden!</p>
              </div>
            </div>

            <div className="mb-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-900 mb-3">Te verwijderen gebruiker:</p>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Naam:</span> {user.first_name}</p>
                  <p><span className="font-medium">Email:</span> {user.email}</p>
                  {user.company_name && (
                    <p><span className="font-medium">Bedrijf:</span> {user.company_name}</p>
                  )}
                </div>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="font-semibold text-red-900 mb-3">Wordt verwijderd:</p>
                <ul className="space-y-2 text-sm text-red-800">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                    {userStats.blogs_count} blog{userStats.blogs_count !== 1 ? 's' : ''}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                    {userStats.campaigns_count} campaign{userStats.campaigns_count !== 1 ? 's' : ''}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                    {userStats.contacts_count} contact{userStats.contacts_count !== 1 ? 'en' : ''}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                    {userStats.storage_mb} MB storage (alle images)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                    Alle AI logs, credit logs, en andere data
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Typ het emailadres <span className="font-mono text-red-600">{user.email}</span> om te bevestigen:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Typ emailadres om te bevestigen"
                  disabled={deleting}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={deleting}
              >
                Annuleren
              </button>
              <button
                onClick={handleDeleteUser}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleting || deleteConfirmText !== user.email}
              >
                {deleting ? 'Verwijderen...' : 'Permanent Verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function KlanttrajectTab({ user, onMutated }: { user: any; onMutated: () => void }) {
  const [showPersonalMail, setShowPersonalMail] = useState(false);
  const [personalMailStepKey, setPersonalMailStepKey] = useState<string | null>(null);
  const [hasFailedOrManual, setHasFailedOrManual] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('user_journey_progress')
        .select('status')
        .eq('user_id', user.id)
        .in('status', ['failed', 'pending_manual']);
      if (!cancelled) setHasFailedOrManual((data ?? []).length > 0);
    })();
    return () => { cancelled = true; };
  }, [user.id, refreshKey]);

  return (
    <div className="space-y-4">
      <JourneyStatusStrip
        userId={user.id}
        journeyStatus={user.journey_status ?? 'inactive'}
        lastActivityAt={user.journey_last_activity_at ?? null}
        hasFailedOrPendingManual={hasFailedOrManual}
        onPersonalMailClick={() => { setPersonalMailStepKey(null); setShowPersonalMail(true); }}
        onMutated={() => { onMutated(); setRefreshKey(k => k + 1); }}
      />
      <JourneyMilestonesBlock userId={user.id} refreshKey={refreshKey} />
      <JourneyTimeline
        userId={user.id}
        refreshKey={refreshKey}
        onPersonalMailFor={(stepKey) => { setPersonalMailStepKey(stepKey); setShowPersonalMail(true); }}
      />
      <JourneySignalsBlock userId={user.id} />
      <JourneyNotesBlock userId={user.id} />

      {showPersonalMail && (
        <PersonalMailModal
          userId={user.id}
          userEmail={user.email}
          relatedStepKey={personalMailStepKey}
          onClose={() => setShowPersonalMail(false)}
          onSent={() => { setRefreshKey(k => k + 1); onMutated(); }}
        />
      )}
    </div>
  );
}
